import { CdkTableModule } from '@angular/cdk/table';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import {
  MatLegacyTableDataSource as MatTableDataSource,
  MatLegacyTableModule as MatTableModule,
} from '@angular/material/legacy-table';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { FuseUtils } from '@fuse/utils';
import {
  BazisMaterialHelper,
  ClientMaterialCutting,
  CustomFormsModule,
  CuttingClientMaterial,
  DialogsService,
  DirectivesModule,
  NgxLocalizedNumbers,
} from 'cloud-shared-lib';
import { Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';

import { ClientMaterialService } from '@cutting/main/shared/services/client-material.service';
import { UserSettingsService } from '@cutting/main/shared/services/user-settings.service';
import { OrderDetailService } from '../order-detail.service';
import { MaterialStandardSizesDialogComponent } from './material-standard-sizes-dialog/material-standard-sizes-dialog.component';

type TableCol = 'height' | 'width' | 'count';

@Component({
  selector: 'cutting-client-materials',
  templateUrl: './client-materials.component.html',
  styleUrls: ['./client-materials.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    FormsModule,
    AsyncPipe,
    NgxLocalizedNumbers,
    CustomFormsModule,
    MatDividerModule,
    MatIconModule,
    CdkTableModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule,
    MatListModule,
    DirectivesModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ClientMaterialsComponent implements OnInit, OnDestroy {
  @Input() disabled = false;

  displayedColumns = ['height', 'width', 'count', 'production', 'square', 'delete'];
  fieldWithErrors: Set<string> = new Set();
  dataSource: MatTableDataSource<ClientMaterialCutting> = new MatTableDataSource();

  materialsLoader$: Observable<CuttingClientMaterial[]>;
  materials: CuttingClientMaterial[] = [];

  currentMaterial: CuttingClientMaterial;
  hasProduction = false;

  action$: Observable<unknown>;
  unsubscribe$ = new Subject<void>();
  showStandardMaterialSizes$ = this.userSettingsService
    .getCuttingSettings()
    .pipe(map(s => s.showStandardMaterialSizes));

  constructor(
    private clientMaterialService: ClientMaterialService,
    public dialog: MatDialog,
    private orderDetailService: OrderDetailService,
    private dialogsService: DialogsService,
    private userSettingsService: UserSettingsService
  ) {}

  ngOnInit(): void {
    this.materialsLoader$ = this.clientMaterialService.materials$.pipe(
      tap(m => (this.materials = m)),
      tap(m => (this.hasProduction = (m || []).some(item => item.cuttings.some(c => c.production))))
    );

    this.orderDetailService.onOrderChanged
      .pipe(
        filter(order => order === false || Boolean(order?.id)),
        take(1),
        takeUntil(this.unsubscribe$),
        tap(order => {
          if (order) {
            this.clientMaterialService.loadClientMaterials(order.id);
          } else {
            this.clientMaterialService.materials$.next([]);
          }
        })
      )
      .subscribe();
  }

  deleteMaterial(mat: ClientMaterialCutting): void {
    const index = this.currentMaterial.cuttings.indexOf(mat);

    if (index >= 0 && !this.currentMaterial.cuttings[index].production) {
      this.currentMaterial.cuttings.splice(index, 1);
      this.dataSource.data = this.currentMaterial.cuttings;

      this.clientMaterialService.editClientMaterialCuttings(this.currentMaterial);
    }
  }

  setCurrentMaterial(item: CuttingClientMaterial): void {
    this.currentMaterial = item;
    this.dataSource.data = item.cuttings;
  }

  getMaterialName(material: CuttingClientMaterial): string {
    return BazisMaterialHelper.formatMaterialNameAndArticle(material.name, material.article);
  }

  updateMaterial(): void {
    this.clientMaterialService.editClientMaterialCuttings(this.currentMaterial);
  }

  getError(errors: Record<string, unknown>, field: TableCol, rowIndex: number): string {
    const fieldId = field + rowIndex;

    if (!errors) {
      this.fieldWithErrors.delete(fieldId);
      return '';
    }
    this.fieldWithErrors.add(fieldId);

    if (errors.range) {
      const from = (errors.range as { value: number }).value[0];
      const to = (errors.range as { value: number }).value[1];

      return $localize`Число должно находиться в диапазоне от ${from} до ${to}`;
    }

    if (errors.required) {
      return $localize`Поле не должно быть пустым`;
    }

    if (errors.number) {
      return $localize`Введено не число`;
    }
  }

  addCutting(): void {
    const guid =
      FuseUtils.generateGUID() + FuseUtils.generateGUID() + FuseUtils.generateGUID() + FuseUtils.generateGUID();
    this.currentMaterial.cuttings.push({
      id: guid,
      height: 0,
      width: 0,
      count: 1,
      production: false,
      standard: false,
    } as ClientMaterialCutting);
    this.dataSource.data = this.currentMaterial.cuttings;
    this.focusLastMaterial();

    this.clientMaterialService.editClientMaterialCuttings(this.currentMaterial);
  }

  addStandardCutting(): void {
    this.action$ = this.dialogsService
      .component(
        MaterialStandardSizesDialogComponent,
        {
          orderId: this.orderDetailService.order?.id,
          material: this.currentMaterial,
        },
        300
      )
      .pipe(
        filter(res => !!res),
        tap(res => {
          const guid =
            FuseUtils.generateGUID() + FuseUtils.generateGUID() + FuseUtils.generateGUID() + FuseUtils.generateGUID();
          this.currentMaterial.cuttings.push({
            id: guid,
            height: res.length,
            width: res.width,
            count: 1,
            production: false,
            standard: true,
          } as ClientMaterialCutting);
          this.dataSource.data = this.currentMaterial.cuttings;

          this.clientMaterialService.editClientMaterialCuttings(this.currentMaterial);
        })
      );
  }

  getSquare(item: ClientMaterialCutting): number {
    return (item.width && item.height ? item.width * item.height : 0) * item.count * 1e-6;
  }

  private focusLastMaterial(): void {
    const inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
      '.client-materials-table .row-client-material-table .cdk-column-height input'
    );
    if (inputs.length > 0) {
      inputs[inputs.length - 1].focus();
      inputs[inputs.length - 1].select();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
